import { useState, useRef, useEffect } from 'react'
import { Info, BookOpen, ChevronRight, Gift } from 'lucide-react'
import Modal from '../UI/Modal'
import { useT } from '../../hooks/useTranslation'
import { useDashboardStore } from '../../store/dashboardStore'
import { useHaptic } from '../../hooks/useHaptic'
import { setUserLanguage, setUserNotifications, getReferralSummary } from '../../services/api'

function Toggle({ on, onChange }) {
  return (
    <button
      onClick={onChange}
      role="switch"
      aria-checked={on}
      style={{
        width: 50, height: 30, borderRadius: 15,
        background: on ? 'linear-gradient(135deg, #34D399 0%, #10B981 100%)' : '#2A2A2A',
        position: 'relative', flexShrink: 0,
        transition: 'background 0.25s', border: 'none', cursor: 'pointer',
      }}
    >
      <div style={{
        position: 'absolute', top: 4,
        left: on ? 24 : 4,
        width: 22, height: 22, borderRadius: '50%',
        background: '#fff', transition: 'left 0.25s',
        boxShadow: '0 1px 4px rgba(0,0,0,0.3)',
      }} />
    </button>
  )
}

function SectionLabel({ children }) {
  return (
    <p style={{
      fontSize: 13, fontWeight: 600, color: '#8E8E93',
      marginBottom: 10, textTransform: 'uppercase', letterSpacing: '0.5px',
    }}>
      {children}
    </p>
  )
}

// $3.5 → '$3.50' (суммы рефералки приходят уже округлёнными до копеек).
const usd = n => `$${(Number(n) || 0).toFixed(2)}`

// '2026-09-05' → '05.09.2026' (сервер отдаёт дату выплаты ISO).
const fmtDate = iso => {
  const m = /^(\d{4})-(\d{2})-(\d{2})$/.exec(iso || '')
  return m ? `${m[3]}.${m[2]}.${m[1]}` : (iso || '')
}

export default function SettingsModal({ onClose }) {
  const modalRef = useRef(null)
  const t = useT()
  const haptic = useHaptic()
  const language = useDashboardStore(s => s.language)
  const setLanguage = useDashboardStore(s => s.setLanguage)
  const setActiveModal = useDashboardStore(s => s.setActiveModal)
  const savedNotifications = useDashboardStore(s => s.notifications)
  const setNotifications = useDashboardStore(s => s.setNotifications)
  const hintsEnabled = useDashboardStore(s => s.hintsEnabled)
  const setHintsEnabled = useDashboardStore(s => s.setHintsEnabled)

  const [langDraft, setLangDraft] = useState(language)
  const [draft, setDraft] = useState({ ...savedNotifications })
  const [hintsDraft, setHintsDraft] = useState(hintsEnabled)
  const [hintsTip, setHintsTip] = useState(false)
  const toggle = id => { haptic.medium(); setDraft(p => ({ ...p, [id]: !p[id] })) }

  // Реферальная программа (п.4.4 ПЛАН_МАРКЕТИНГА, 2026-09-07): сводка тянется при
  // КАЖДОМ открытии Настроек — код на сервере создаётся лениво (первый показ),
  // а суммы меняются после оплат друзей. Ошибка сети — молча: блок остаётся
  // «Загрузка…», приложение не падает.
  const [refData, setRefData] = useState(null)
  const [refCopied, setRefCopied] = useState(false)
  useEffect(() => {
    let alive = true
    getReferralSummary().then(d => { if (alive) setRefData(d) }).catch(() => {})
    return () => { alive = false }
  }, [])

  const openLegal = () => { haptic.medium(); setActiveModal('legal') }
  const copyRefLink = () => {
    if (!refData || !refData.link) return
    haptic.medium()
    try {
      if (navigator.clipboard?.writeText) navigator.clipboard.writeText(refData.link).catch(() => {})
    } catch { /* буфер обмена недоступен — ссылка остаётся в поле, её можно выделить */ }
    setRefCopied(true)
    setTimeout(() => setRefCopied(false), 1600)
  }

  // Проверяем есть ли изменения (раздел «Ежедневный отчёт» убран совсем —
  // отчёт происходит всегда, тумблера у него больше нет).
  const hasChanges = langDraft !== language ||
    draft.trades !== savedNotifications.trades ||
    draft.funding !== savedNotifications.funding ||
    hintsDraft !== hintsEnabled

  const handleSave = () => {
    if (!hasChanges) return
    haptic.success()
    setLanguage(langDraft)
    setNotifications(draft)
    setHintsEnabled(hintsDraft)
    // Уведомления синхронизируем с бэкендом: бот (notifier) шлёт сообщения по
    // этим флагам. Каждый изменённый тумблер уходит отдельным PATCH-ом
    // (back-end обновляет только переданные поля).
    const notifPatch = {}
    if (draft.trades !== savedNotifications.trades) notifPatch.trades = draft.trades
    if (draft.funding !== savedNotifications.funding) notifPatch.funding = draft.funding
    if (Object.keys(notifPatch).length) {
      setUserNotifications(notifPatch).catch(err => console.error('Failed to save notifications:', err))
    }
    // Синхронизируем язык с бэкендом (бот использует его же).
    // Без window.location.reload() — интерфейс переключается мгновенно.
    if (langDraft !== language) {
      setUserLanguage(langDraft).catch(err => console.error('Failed to save language:', err))
    }
    modalRef.current?.close()
  }

  const notifItems = [
    { id: 'trades',  label: t.tradingSignals,       desc: t.tradingSignalsDesc },
    // п.8 task.txt: уведомления о фандингах за 5 минут до выплаты, можно отключить.
    { id: 'funding', label: t.fundingNotifications, desc: t.fundingNotificationsDesc },
  ]

  const saveBtn = (
    <button
      onClick={handleSave}
      disabled={!hasChanges}
      style={{
        width: '100%', padding: '14px', borderRadius: 14,
        background: hasChanges
          ? 'linear-gradient(135deg, #34D399 0%, #10B981 100%)'
          : 'rgba(255,255,255,0.06)',
        color: hasChanges ? '#fff' : '#636366',
        fontSize: 15, fontWeight: 700,
        border: 'none', cursor: hasChanges ? 'pointer' : 'default',
        transition: 'all 0.2s',
      }}
    >
      {t.save}
    </button>
  )

  return (
    <Modal ref={modalRef} title={t.settingsTitle} onClose={onClose} footer={saveBtn}>
      <div>

        {/* Инструкция — полное руководство по использованию бота.
            Контент читается с сервера из markdown-файла (TG-BOT/INSTRUCTION.md)
            при каждом открытии: правки владельца видны сразу. data-hint —
            цель для шага онбординг-тура. */}
        <button
          data-hint="settings-instruction"
          onClick={() => { haptic.medium(); setActiveModal('instruction') }}
          style={{
            width: '100%', display: 'flex', alignItems: 'center', gap: 14,
            background: 'rgba(16,185,129,0.06)',
            border: '1px solid rgba(16,185,129,0.18)',
            borderRadius: 14, padding: '14px 16px', marginBottom: 28,
            cursor: 'pointer', textAlign: 'left',
            WebkitTapHighlightColor: 'transparent',
          }}
        >
          <div style={{
            width: 40, height: 40, borderRadius: 12, flexShrink: 0,
            background: 'linear-gradient(135deg, #34D399 0%, #059669 100%)',
            display: 'flex', alignItems: 'center', justifyContent: 'center',
          }}>
            <BookOpen size={20} color="#fff" />
          </div>
          <div style={{ flex: 1, minWidth: 0 }}>
            <div style={{ fontSize: 15, fontWeight: 600, color: '#fff' }}>{t.instructionTitle}</div>
            <div style={{ fontSize: 12, color: '#636366', marginTop: 2 }}>{t.instructionSubtitle}</div>
          </div>
          <ChevronRight size={18} color="#636366" style={{ flexShrink: 0 }} />
        </button>

        {/* Language */}
        <SectionLabel>{t.languageLabel}</SectionLabel>
        <div style={{ display: 'flex', gap: 8, marginBottom: 28 }}>
          {[{ code: 'en', label: 'English' }, { code: 'ru', label: 'Русский' }].map(({ code, label }) => (
            <button
              key={code}
              onClick={() => { haptic.light(); setLangDraft(code) }}
              style={{
                flex: 1, padding: '11px 0', borderRadius: 12, fontSize: 14, fontWeight: 600,
                background: langDraft === code ? 'linear-gradient(135deg, #34D399 0%, #10B981 100%)' : '#1A1A1A',
                color: langDraft === code ? '#fff' : '#636366',
                border: langDraft === code ? 'none' : '1px solid rgba(255,255,255,0.06)',
                cursor: 'pointer', transition: 'all 0.2s',
              }}
            >
              {label}
            </button>
          ))}
        </div>

        {/* Notifications */}
        <SectionLabel>{t.notificationsLabel}</SectionLabel>
        {notifItems.map(({ id, label, desc }, i) => (
          <div
            key={id}
            style={{
              display: 'flex', alignItems: 'center', justifyContent: 'space-between',
              padding: '14px 0',
              borderBottom: i < notifItems.length - 1 ? '1px solid rgba(255,255,255,0.05)' : 'none',
            }}
          >
            <div style={{ flex: 1, marginRight: 16 }}>
              <div style={{ fontSize: 15, fontWeight: 500, color: '#fff' }}>{label}</div>
              <div style={{ fontSize: 12, color: '#636366', marginTop: 2 }}>{desc}</div>
            </div>
            <Toggle on={draft[id]} onChange={() => toggle(id)} />
          </div>
        ))}

        {/* Реферальная программа (п.4.4 ПЛАН_МАРКЕТИНГА, 2026-09-07): ссылка-
            приглашение t.me/<бот>?start=ref_<код> (привязывает бот при /start)
            + мини-статистика.
            Награда — 10% с ПЕРВОЙ оплаченной подписки друга (условия — в
            LegalModal, кнопка ниже; тот же блок есть в «Условиях» внизу аппа). */}
        <SectionLabel>{t.settingsReferralLabel}</SectionLabel>
        <div style={{
          background: 'rgba(255,255,255,0.04)',
          border: '1px solid rgba(255,255,255,0.07)',
          borderRadius: 14, padding: 14, marginBottom: 28,
        }}>
          {!refData ? (
            <div style={{ fontSize: 13, color: '#636366' }}>{t.referralLoading}</div>
          ) : (
            <>
              <div style={{ display: 'flex', alignItems: 'center', gap: 10, marginBottom: 12 }}>
                <div style={{
                  width: 34, height: 34, borderRadius: 10, flexShrink: 0,
                  background: 'linear-gradient(135deg, #34D399 0%, #059669 100%)',
                  display: 'flex', alignItems: 'center', justifyContent: 'center',
                }}>
                  <Gift size={18} color="#fff" />
                </div>
                <div style={{ flex: 1, fontSize: 13, color: '#8E8E93', lineHeight: 1.45 }}>
                  {t.referralInviteTo}
                </div>
              </div>

              {refData.link && (
                <>
                  <div style={{
                    fontSize: 10.5, fontWeight: 600, color: '#636366',
                    textTransform: 'uppercase', letterSpacing: '0.5px', marginBottom: 6,
                  }}>
                    {t.referralLinkLabel}
                  </div>
                  <div style={{ display: 'flex', gap: 8, marginBottom: 12 }}>
                    <div style={{
                      flex: 1, minWidth: 0,
                      background: 'rgba(0,0,0,0.25)',
                      border: '1px solid rgba(255,255,255,0.06)',
                      borderRadius: 10, padding: '9px 10px',
                      fontSize: 11, color: '#34D399',
                      overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap',
                    }} title={refData.link}>
                      {refData.link}
                    </div>
                    <button
                      onClick={copyRefLink}
                      style={{
                        flexShrink: 0, padding: '0 14px', borderRadius: 10,
                        border: 'none', cursor: 'pointer', fontSize: 13, fontWeight: 600,
                        color: refCopied ? '#0A0A0A' : '#fff',
                        background: refCopied
                          ? 'linear-gradient(135deg, #34D399 0%, #10B981 100%)'
                          : 'rgba(255,255,255,0.09)',
                        transition: 'all 0.2s',
                        WebkitTapHighlightColor: 'transparent',
                      }}
                    >
                      {refCopied ? t.copied : t.copy}
                    </button>
                  </div>
                </>
              )}

              <div style={{ display: 'flex', gap: 8 }}>
                {[
                  { v: String(refData.invitedCount), cap: t.referralFriendsInvited },
                  { v: usd(refData.earnedAll), cap: t.referralTotalEarned },
                ].map((s, i) => (
                  <div key={i} style={{
                    flex: 1,
                    background: 'rgba(0,0,0,0.22)',
                    border: '1px solid rgba(255,255,255,0.05)',
                    borderRadius: 10, padding: '8px 10px',
                  }}>
                    <div style={{ fontSize: 16, fontWeight: 700, color: '#fff', lineHeight: 1.2 }}>
                      {s.v}
                    </div>
                    <div style={{ fontSize: 10.5, color: '#636366', marginTop: 2 }}>{s.cap}</div>
                  </div>
                ))}
              </div>

              {/* «К выплате 5-го числа»: вознаграждение за прошлый месяц, которое
                  ещё не выплачено (владелец переводит до 5-го текущего месяца). */}
              {refData.duePrev > 0 && (
                <div style={{
                  display: 'flex', alignItems: 'center', gap: 8,
                  background: 'rgba(16,185,129,0.1)',
                  border: '1px solid rgba(16,185,129,0.25)',
                  borderRadius: 10, padding: '9px 12px', marginTop: 10,
                }}>
                  <span style={{ fontSize: 14, fontWeight: 700, color: '#34D399' }}>
                    {usd(refData.duePrev)}
                  </span>
                  <span style={{ fontSize: 12, color: '#8E8E93', flex: 1 }}>
                    {t.referralDue} · {fmtDate(refData.dueDate)}
                  </span>
                </div>
              )}

              <div style={{
                borderTop: '1px solid rgba(255,255,255,0.06)',
                marginTop: 10, paddingTop: 10,
                display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 8,
              }}>
                <span style={{ fontSize: 11, color: '#636366', lineHeight: 1.5, flex: 1 }}>
                  {(refData.earnedAll > 0 || refData.duePrev > 0)
                    ? t.referralPayoutNote
                    : t.referralNoFriends}
                </span>
                <button
                  onClick={openLegal}
                  style={{
                    flexShrink: 0, background: 'none', border: 'none', cursor: 'pointer',
                    color: '#34D399', fontSize: 12, fontWeight: 600,
                    padding: 4, WebkitTapHighlightColor: 'transparent',
                  }}
                >
                  {t.referralTerms} ›
                </button>
              </div>
            </>
          )}
        </div>

        {/* Hints (онбординг-тур): слово «Подсказки» + ползунок, ничего лишнего.
            По наведению/нажатию на слово всплывает тултип-объяснение — так же,
            как тултип у иконки Info возле значений (см. ProfitSection).
            ВАЖНО (баг на ПК): обработчики мыши висят на ВНЕШНЕЙ обёртке, а не на
            кнопке. Раньше они были на кнопке, а тултип висел под ней с зазором
            8px — мышь пересекала этот зазор, кнопка «отпускала» её (onMouseLeave),
            и подсказка исчезала, не успев появиться. Теперь тултип примыкает к
            обёртке (top: 100% без зазора), а визуальный «воздух» — это его верхний
            padding внутри его же hover-области, поэтому мышь не выпадает. */}
        <div
          data-hint="settings-hints"
          style={{ position: 'relative' }}
          onMouseEnter={() => setHintsTip(true)}
          onMouseLeave={() => setHintsTip(false)}
        >
          <div style={{
            display: 'flex', alignItems: 'center', justifyContent: 'space-between',
            padding: '14px 0',
            borderBottom: '1px solid rgba(255,255,255,0.05)',
          }}>
            <button
              onClick={() => { haptic.light(); setHintsTip(v => !v) }}
              style={{
                flex: 1, display: 'flex', alignItems: 'center', gap: 6,
                background: 'none', border: 'none', cursor: 'pointer',
                padding: 0, textAlign: 'left',
                WebkitTapHighlightColor: 'transparent',
              }}
            >
              <span style={{ fontSize: 15, fontWeight: 500, color: hintsTip ? '#34D399' : '#fff' }}>
                {t.hintsLabel}
              </span>
              <Info size={15} color={hintsTip ? '#34D399' : '#636366'} />
            </button>

            <Toggle
              on={hintsDraft}
              onChange={() => { haptic.medium(); setHintsDraft(v => !v) }}
            />
          </div>

          {hintsTip && (
            <div style={{
              position: 'absolute',
              top: '100%',
              left: 0, right: 0,
              zIndex: 100,
              background: '#1C1C1E',
              border: '1px solid rgba(255,107,53,0.25)',
              borderRadius: 12,
              padding: '8px 14px 10px',
              boxShadow: '0 8px 32px rgba(0,0,0,0.6)',
            }}>
              {/* Стрелка */}
              <div style={{
                position: 'absolute',
                top: -6, left: 22,
                width: 10, height: 10,
                background: '#1C1C1E',
                border: '1px solid rgba(255,107,53,0.25)',
                borderRight: 'none',
                borderBottom: 'none',
                transform: 'rotate(45deg)',
              }} />
              <p style={{ fontSize: 13, color: '#8E8E93', lineHeight: 1.55, margin: 0 }}>
                {t.hintsDesc}
              </p>
            </div>
          )}
        </div>

      </div>
    </Modal>
  )
}
